import React, { lazy } from 'react';
import { FullProject, ProjectMetadata } from '../../types';

const GLOBAL_VIEW_IDS = [
  'dashboard',
  'metricsDashboard',
  'globalKanban',
  'communication',
  'impactEffort',
  'noDateTasks',
  'global',
  'globalCalendar',
  'aiAssistant',
  'analysis',
  'projectPool',
  'globalGantt',
];
const PERSISTENT_PROJECT_VIEWS = [
  'kanban',
  'gantt',
  'list',
  'calendar',
  'projectContact',
  'timeline',
  'mindmap',
  'analysis',
  'projectDocuments',
];

const ProjectOverviewModal = lazy(() => import('../ProjectOverviewModal'));
const ProjectDuplicateModal = lazy(() => import('../ProjectDuplicateModal'));
const ProjectDeleteModal = lazy(() => import('../ProjectDeleteModal'));
const ProjectSettingsModal = lazy(() => import('../ProjectSettingsModal'));
const ShareProjectModal = lazy(() =>
  import('../ShareProjectModal').then((m) => ({ default: m.ShareProjectModal }))
);

export interface ProjectManagementModalsGroupProps {
  showProjectOverviewModal: boolean;
  setShowProjectOverviewModal: (open: boolean) => void;
  allProjects: FullProject[];
  projects: FullProject[];
  activeWorkspace?: 'professional' | 'private';
  currentProjectId: string;
  setCurrentProjectId: (id: string) => void;
  viewMode: string;
  setViewMode: (mode: any) => void;
  updateProjectCategory: (projectId: string, categoryId?: string) => void;
  globalMetadata: any;
  updateProjectMetadata: (projectId: string, updates: Partial<ProjectMetadata>) => void;
  reorderProjects: (newOrder: FullProject[]) => void;
  projectToDuplicate: string | null;
  setProjectToDuplicate: (id: string | null) => void;
  confirmDuplicateProject: (
    id: string,
    ref: 'milestone' | 'start' | 'end',
    date: string,
    milestoneId: string | number | null,
    newName?: string
  ) => void;
  projectToDelete: string | null;
  setProjectToDelete: (id: string | null) => void;
  deleteProject: (id: string) => void | Promise<void>;
  projectToRename: string | null;
  setProjectToRename: (id: string | null) => void;
  renameProject: (
    id: string,
    name: string,
    color: string,
    visibility?: 'public' | 'private'
  ) => void;
  categories: any[];
  shareProjectWithUser: (
    projectId: string,
    email: string,
    role: 'viewer' | 'editor'
  ) => Promise<void>;
  unshareProjectWithUser: (projectId: string, email: string) => Promise<void>;
  updateProjectShareRole: (
    projectId: string,
    email: string,
    role: 'viewer' | 'editor'
  ) => Promise<void>;
  archiveProject: (id: string) => void;
  unarchiveProject: (id: string) => void;
  currentUserId: string;
  projectToShare: string | null;
  setProjectToShare: (id: string | null) => void;
  userEmail?: string;
}

export const ProjectManagementModalsGroup: React.FC<ProjectManagementModalsGroupProps> = ({
  showProjectOverviewModal,
  setShowProjectOverviewModal,
  allProjects,
  projects,
  activeWorkspace,
  currentProjectId,
  setCurrentProjectId,
  viewMode,
  setViewMode,
  updateProjectCategory,
  globalMetadata,
  updateProjectMetadata,
  reorderProjects,
  projectToDuplicate,
  setProjectToDuplicate,
  confirmDuplicateProject,
  projectToDelete,
  setProjectToDelete,
  deleteProject,
  projectToRename,
  setProjectToRename,
  renameProject,
  categories,
  shareProjectWithUser,
  unshareProjectWithUser,
  updateProjectShareRole,
  archiveProject,
  unarchiveProject,
  currentUserId,
  projectToShare,
  setProjectToShare,
  userEmail,
}) => {
  const renamedProject = projectToRename
    ? projects.find((p) => p.id === projectToRename) || null
    : null;
  const sharedProject = projectToShare
    ? projects.find((p) => p.id === projectToShare) || null
    : null;

  return (
    <>
      {showProjectOverviewModal && (
        <ProjectOverviewModal
          isOpen={showProjectOverviewModal}
          onClose={() => setShowProjectOverviewModal(false)}
          projects={allProjects.filter((p) => {
            const id = p.id.toLowerCase();
            return (
              !id.startsWith('inbox_inforum') &&
              !id.startsWith('comm_internal') &&
              !['inbox_inforum', 'comm_internal'].some((x) => id.includes(x))
            );
          })}
          activeWorkspace={activeWorkspace || 'professional'}
          currentProjectId={currentProjectId}
          onSelectProject={(id) => {
            setCurrentProjectId(id);
            const selectedProject = allProjects.find((p) => p.id === id);
            if (selectedProject && GLOBAL_VIEW_IDS.includes(viewMode)) {
              const lastView = localStorage.getItem(`gwantt_last_view_${id}`);
              setViewMode(
                lastView && PERSISTENT_PROJECT_VIEWS.includes(lastView)
                  ? (lastView as any)
                  : 'gantt'
              );
            }
          }}
          onUpdateProjectCategory={updateProjectCategory}
          globalMetadata={globalMetadata}
          onUpdateGlobalMetadata={(updates) => updateProjectMetadata('inbox_inforum', updates)}
          onReorderProjects={reorderProjects}
        />
      )}

      {projectToDuplicate && (
        <ProjectDuplicateModal
          project={projects.find((p) => p.id === projectToDuplicate) || null}
          onClose={() => setProjectToDuplicate(null)}
          onConfirm={confirmDuplicateProject}
        />
      )}

      {projectToDelete && (
        <ProjectDeleteModal
          projectId={projectToDelete}
          onClose={() => setProjectToDelete(null)}
          onConfirm={deleteProject}
          projectName={projects.find((p) => p.id === projectToDelete)?.name || ''}
        />
      )}

      {projectToRename && (
        <ProjectSettingsModal
          projectId={projectToRename}
          onClose={() => setProjectToRename(null)}
          onConfirm={(id, name, color, visibility) => renameProject(id, name, color, visibility)}
          initialName={renamedProject?.name || ''}
          initialColor={renamedProject?.color || 'bg-blue-500'}
          initialCategoryId={renamedProject?.categoryId}
          initialVisibility={renamedProject?.visibility || 'private'}
          categories={categories}
          onAssignCategory={updateProjectCategory}
          onDuplicate={(id) => setProjectToDuplicate(id)}
          project={renamedProject}
          onShare={shareProjectWithUser}
          onUnshare={unshareProjectWithUser}
          onUpdateRole={updateProjectShareRole}
          onArchiveToggle={(id) => {
            const p = projects.find((proj) => proj.id === id);
            if (p) {
              if (p.archived) {
                unarchiveProject(id);
              } else {
                archiveProject(id);
              }
            }
          }}
          onDelete={(id) => setProjectToDelete(id)}
          isSystem={
            [
              'kommunikation',
              'inbox_inforum',
              'posteingang',
              'communication',
              'comm_internal',
              'inbox',
            ].includes(projectToRename.toLowerCase()) ||
            projectToRename.toLowerCase().startsWith('inbox_inforum') ||
            projectToRename.toLowerCase().startsWith('comm_internal') ||
            (renamedProject?.name || '').toLowerCase().includes('kommunikation') ||
            (renamedProject?.name || '').toLowerCase().includes('posteingang') ||
            (renamedProject?.name || '').toLowerCase().includes('inbox')
          }
          isArchived={!!renamedProject?.archived}
          projectOwnerId={renamedProject?.userId}
          userId={currentUserId}
          setProjectToShare={setProjectToShare}
          isReadOnly={(() => {
            if (!renamedProject) return true;
            if (renamedProject.userId === currentUserId) return false;
            const email = userEmail?.toLowerCase();
            if (!email) return true;
            if (!renamedProject.sharedEditors) {
              return !renamedProject.sharedWith?.includes(email);
            }
            return !renamedProject.sharedEditors.includes(email);
          })()}
        />
      )}

      {projectToShare && sharedProject && (
        <ShareProjectModal
          isOpen={!!projectToShare}
          onClose={() => setProjectToShare(null)}
          project={sharedProject}
          onShare={shareProjectWithUser}
          onUnshare={unshareProjectWithUser}
          onUpdateRole={updateProjectShareRole}
        />
      )}
    </>
  );
};
